(() => {
  const STORAGE_KEY = 'bm_vehicle_garage_v1';
  const IMAGE_CACHE_KEY = 'bm_generation_image_cache_v1';
  const FITMENT_SECTION_ID = 'bm-fitment-options';
  const VERSION = 1;
  const compatibilityResolvers = new Map();
  let memoryState = { version: VERSION, active: null };
  let garageDetailRequest = 0;

  const safeParse = (value) => { try { return JSON.parse(value); } catch (error) { return null; } };

  const readState = () => {
    try {
      const stored = safeParse(window.localStorage.getItem(STORAGE_KEY));
      if (stored && stored.version === VERSION) memoryState = stored;
    } catch (error) {}
    return memoryState;
  };

  const writeState = (nextState) => {
    memoryState = nextState;
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState)); } catch (error) {}
  };

  const readImageCache = () => {
    try { return safeParse(window.localStorage.getItem(IMAGE_CACHE_KEY)) || {}; }
    catch (error) { return {}; }
  };

  const writeImageCache = (generationId, imageUrl) => {
    if (!generationId || !imageUrl) return;
    try {
      const cache = readImageCache();
      cache[String(generationId)] = imageUrl;
      const keys = Object.keys(cache);
      if (keys.length > 30) keys.slice(0, keys.length - 30).forEach((key) => delete cache[key]);
      window.localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {}
  };

  const cleanNode = (node) => {
    if (!node) return null;
    const cleaned = {
      id: String(node.id || ''),
      name: String(node.name || ''),
      url: String(node.url || '')
    };
    if (node.image) cleaned.image = String(node.image);
    if (node.logo) cleaned.logo = String(node.logo);
    if (node.wheel) cleaned.wheel = String(node.wheel);
    if (node.years) cleaned.years = String(node.years);
    if (node.codes) cleaned.codes = String(node.codes);
    return cleaned;
  };

  const cleanNewVehicle = (value) => {
    if (!value?.vehicleId) return null;
    const yearFrom = Number(value.yearFrom);
    const yearTo = Number(value.yearTo);
    return {
      vehicleId: String(value.vehicleId),
      brandRef: value.brandRef ? String(value.brandRef) : null,
      modelRef: value.modelRef ? String(value.modelRef) : null,
      label: String(value.label || ''),
      brandLabel: String(value.brandLabel || ''),
      modelLabel: String(value.modelLabel || ''),
      yearFrom: Number.isFinite(yearFrom) && yearFrom > 0 ? yearFrom : null,
      yearTo: Number.isFinite(yearTo) && yearTo > 0 ? yearTo : null,
      vehiclePageUrl: value.vehiclePageUrl ? String(value.vehiclePageUrl) : null,
      image: value.image ? String(value.image) : null
    };
  };

  const normalizeVehicle = (vehicle) => {
    const newVehicle = cleanNewVehicle(vehicle?.newVehicle);
    const generation = cleanNode(vehicle?.generation);
    if (!generation?.id && !newVehicle?.vehicleId) return null;
    return {
      source: vehicle.source || (generation?.id ? 'internal-generation' : 'bridge-vehicle'),
      brand: cleanNode(vehicle.brand),
      model: cleanNode(vehicle.model),
      generation,
      newVehicle,
      providerIds: vehicle.providerIds && typeof vehicle.providerIds === 'object' ? vehicle.providerIds : {},
      selectedAt: Number(vehicle.selectedAt || Date.now())
    };
  };

  const getActive = () => readState().active || null;
  const getNewVehicle = () => getActive()?.newVehicle || null;
  const currentPreviewThemeId = () => {
    let id = '';
    try { id = new URL(window.location.href).searchParams.get('preview_theme_id') || ''; } catch (_) {}
    if (!/^\d+$/.test(id) && window.Shopify?.theme?.role === 'unpublished') {
      id = String(window.Shopify.theme.id || '');
    }
    return /^\d+$/.test(id) ? id : '';
  };
  const nativeExactVehicleHref = (vehicle) => {
    if (!vehicle?.vehicleId || !vehicle.vehiclePageUrl) return '';
    try {
      const current = new URL(window.location.href);
      const target = new URL(vehicle.vehiclePageUrl, current.origin);
      const canonical = target.hostname === 'bandenmeesters.be' || target.hostname === 'www.bandenmeesters.be';
      if (target.protocol !== 'https:' || target.port || target.username || target.password
        || target.search || target.hash || (!canonical && target.origin !== current.origin)
        || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/[^/]+\/[^/]+\/?$/.test(target.pathname)) return '';
      const previewId = currentPreviewThemeId();
      return target.pathname + (previewId ? '?preview_theme_id=' + previewId : '');
    } catch (_) { return ''; }
  };
  const draftVehicleResultsHref = () => {
    const path = '/collections/nieuwe-velgen?browse=vehicle';
    const previewId = currentPreviewThemeId();
    return previewId ? path + '&preview_theme_id=' + previewId : path;
  };

  const vehicleLabel = (vehicle) => {
    if (!vehicle) return '';
    const generation = shortGenerationName(vehicle);
    const years = String(vehicle?.generation?.years || '').trim()
      || inlineGenerationYears(vehicle)
      || normalizeYears(vehicle?.newVehicle?.yearFrom, vehicle?.newVehicle?.yearTo);
    const exactType = String(vehicle?.newVehicle?.label || '').trim();
    return [
      vehicle.brand?.name || vehicle?.newVehicle?.brandLabel || '',
      vehicle.model?.name || vehicle?.newVehicle?.modelLabel || '',
      generation || exactType,
      years
    ].filter(Boolean).join(' ');
  };

  const vehicleTitle = (vehicle) => {
    if (!vehicle) return '';
    return [
      vehicle.brand?.name || vehicle?.newVehicle?.brandLabel || '',
      vehicle.model?.name || vehicle?.newVehicle?.modelLabel || ''
    ].filter(Boolean).join(' ') || vehicleLabel(vehicle);
  };

  const normalizeYears = (yearFrom, yearTo) => {
    const from = String(yearFrom || '').trim();
    const to = String(yearTo || '').trim();
    if (!from) return '';
    return `${from}–${to || 'heden'}`;
  };

  const shortGenerationName = (vehicle) => {
    let value = String(vehicle?.generation?.name || '').trim();
    const modelName = String(vehicle?.model?.name || '').trim();
    if (modelName && value.toLocaleLowerCase('nl').startsWith(modelName.toLocaleLowerCase('nl'))) {
      value = value.slice(modelName.length).trim();
    }
    value = value
      .replace(/\s+vanaf\s+\d{4}\s*$/i, '')
      .replace(/\s+\d{4}\s*[–-]\s*(?:\d{4}|heden)\s*$/i, '')
      .trim();
    return value;
  };

  const inlineGenerationYears = (vehicle) => {
    const raw = String(vehicle?.generation?.name || '');
    const fromMatch = raw.match(/\bvanaf\s+(\d{4})\b/i);
    if (fromMatch) return `vanaf ${fromMatch[1]}`;
    const rangeMatch = raw.match(/\b(\d{4})\s*[–-]\s*(\d{4}|heden)\b/i);
    if (rangeMatch) return `${rangeMatch[1]}–${rangeMatch[2]}`;
    return '';
  };

  const vehicleMeta = (vehicle, showCodes = false) => {
    const parts = [];
    const generation = shortGenerationName(vehicle);
    const exactType = String(vehicle?.newVehicle?.label || '').trim();
    const years = String(vehicle?.generation?.years || '').trim()
      || inlineGenerationYears(vehicle)
      || normalizeYears(vehicle?.newVehicle?.yearFrom, vehicle?.newVehicle?.yearTo);
    const codes = String(vehicle?.generation?.codes || '').trim();
    if (generation || exactType) parts.push(generation || exactType);
    if (years && !parts.includes(years)) parts.push(years);
    if (showCodes && codes && !parts.includes(codes)) parts.push(codes);
    return parts.join(' · ');
  };

  const notify = (evidenceRefresh = false) => {
    renderGarage();
    renderProductCompatibility();
    window.dispatchEvent(new CustomEvent('bm:vehicle:change', { detail: { vehicle: getActive(), evidenceRefresh } }));
  };

  const setActive = (vehicle, { evidenceRefresh = false } = {}) => {
    const normalized = normalizeVehicle(vehicle);
    if (!normalized) return false;
    writeState({ version: VERSION, active: normalized });
    notify(evidenceRefresh);
    return true;
  };

  // Refresh display/navigation evidence for the same exact Bridge Vehicle only.
  // An asynchronous directory response must never replace a newer selection.
  const refreshNewVehicle = (value) => {
    const active = getActive();
    if (!value?.vehicleId) return false;
    const next = cleanNewVehicle({ ...active?.newVehicle, ...value });
    if (!next || active?.newVehicle?.vehicleId !== next.vehicleId) return false;
    const generation = null;
    if (JSON.stringify(cleanNewVehicle(active.newVehicle)) === JSON.stringify(next) && active.generation === generation) return true;
    return setActive({ ...active, newVehicle: next, generation,
      brand: generation ? active.brand : { ...active.brand, name: next.brandLabel },
      model: generation ? active.model : { ...active.model, name: next.modelLabel },
      providerIds: generation ? active.providerIds : {},
    }, { evidenceRefresh: true });
  };

  const clear = () => {
    writeState({ version: VERSION, active: null });
    notify();
  };

  const mergeProviderIds = (providerIds) => {
    const active = getActive();
    if (!active || !providerIds || typeof providerIds !== 'object') return false;
    active.providerIds = { ...(active.providerIds || {}), ...providerIds };
    active.selectedAt = Date.now();
    return setActive(active);
  };

  const normalizePath = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/$/, '') || '/';
    } catch (error) {
      return String(value).split('?')[0].split('#')[0].replace(/\/$/, '');
    }
  };

  compatibilityResolvers.set('internal-generation', (vehicle, context) => {
    if (!vehicle) return null;
    // USED fitment remains owned by Shopify generation/metaobject bindings.
    // An exact Bridge Vehicle generationUrl is already consumed by the cart;
    // its Vehicle UUID itself cannot be treated as a Shopify generation ID.
    if (vehicle.newVehicle) return null;
    const activeUrl = normalizePath(vehicle.generation?.url);
    const legacyIdIsCurrent = !vehicle.newVehicle
      || (activeUrl && normalizePath(vehicle.generation?.url) === activeUrl);
    const ids = String(context.fitmentIds || '').split('|').map(value => value.trim()).filter(Boolean);
    if (ids.length && vehicle.generation?.id && legacyIdIsCurrent) {
      return ids.includes(String(vehicle.generation.id));
    }
    const urls = String(context.fitmentUrls || '').split('|').map(normalizePath).filter(Boolean);
    if (urls.length && activeUrl) return urls.includes(activeUrl);
    // A Vehicle without a verified Shopify generation binding stays unknown.
    return null;
  });

  const registerCompatibilityResolver = (source, resolver) => {
    if (!source || typeof resolver !== 'function') return false;
    compatibilityResolvers.set(source, resolver);
    renderProductCompatibility();
    return true;
  };

  const garageElement = () => document.querySelector('[data-bm-my-vehicle]');
  const rootUrl = () => garageElement()?.dataset.rootUrl || '/';

  const isSelectorAction = (element) => {
    if (!element) return false;
    const href = element.getAttribute?.('href');
    if (href) {
      try {
        const target = new URL(href, window.location.origin);
        return target.origin === window.location.origin && target.hash === '#voertuigselector';
      } catch (_) { return false; }
    }
    return ['data-bm-product-vehicle-selector', 'data-bm-compat-action',
      'data-bm-vehicle-change-link', 'data-bm-vehicle-stock-link']
      .some(name => element.hasAttribute?.(name));
  };

  const selectorUrl = () => {
    const root = rootUrl();
    return `${root}${root.endsWith('/') ? '' : '/'}#voertuigselector`;
  };

  const setGarageImage = (garage, vehicle, imageUrl = '') => {
    const media = garage?.querySelector('[data-bm-my-auto-media]');
    const image = garage?.querySelector('[data-bm-vehicle-image]');
    if (!media || !image) return;

    const showImage = garage.dataset.bmMyAutoShowImage !== 'false';
    const hideMedia = () => {
      media.hidden = true;
      image.hidden = true;
    };

    if (!showImage || !imageUrl) {
      image.onload = null;
      image.onerror = null;
      image.removeAttribute('src');
      image.alt = '';
      hideMedia();
      return;
    }

    image.alt = vehicleTitle(vehicle) || vehicleLabel(vehicle);
    hideMedia();
    image.onload = () => {
      image.hidden = false;
      media.hidden = false;
    };
    image.onerror = () => {
      image.removeAttribute('src');
      image.alt = '';
      hideMedia();
    };
    image.src = imageUrl;
    if (image.complete && image.naturalWidth > 0) {
      image.hidden = false;
      media.hidden = false;
    }
  };

  const findGenerationPageImage = (doc) => {
    const image = doc?.querySelector('.bm-vehicle-hero__visual img:not(.bm-logo-fallback), .bm-generation-image img');
    return image?.getAttribute('src') || '';
  };

  const fetchGenerationDetails = async (vehicle) => {
    const generationId = String(vehicle?.generation?.id || '');
    if (!generationId) return {};

    const details = {
      image: String(vehicle?.generation?.image || readImageCache()[generationId] || ''),
      years: String(vehicle?.generation?.years || ''),
      codes: String(vehicle?.generation?.codes || '')
    };

    if (vehicle?.model?.url && (!details.image || !details.years || !details.codes)) {
      try {
        const endpoint = new URL(vehicle.model.url, window.location.origin);
        endpoint.searchParams.set('section_id', FITMENT_SECTION_ID);
        const response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
        });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const choice = [...doc.querySelectorAll('.bm-choice[data-choice="generation"]')]
            .find((item) => String(item.dataset.id || '') === generationId);
          if (choice) {
            details.image = details.image || choice.dataset.image || choice.querySelector('img')?.getAttribute('src') || '';
            details.years = details.years || normalizeYears(choice.dataset.yearFrom, choice.dataset.yearTo);
            details.codes = details.codes || String(choice.dataset.codes || '');
          }
        }
      } catch (error) {}
    }

    if (!details.image && vehicle?.generation?.url) {
      try {
        const target = new URL(vehicle.generation.url, window.location.origin);
        if (normalizePath(target.pathname) === normalizePath(window.location.pathname)) {
          details.image = findGenerationPageImage(document);
        } else {
          const response = await fetch(target.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }
          });
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            details.image = findGenerationPageImage(doc);
          }
        }
      } catch (error) {}
    }

    if (details.image) writeImageCache(generationId, details.image);
    return details;
  };

  const persistResolvedDetails = (vehicle, details) => {
    const current = getActive();
    if (!current || String(current.generation?.id || '') !== String(vehicle?.generation?.id || '')) return current;

    const enriched = {
      ...current,
      generation: {
        ...(current.generation || {}),
        image: details.image || current.generation?.image || '',
        years: details.years || current.generation?.years || '',
        codes: details.codes || current.generation?.codes || ''
      }
    };
    const normalized = normalizeVehicle(enriched);
    if (normalized) writeState({ version: VERSION, active: normalized });
    return normalized || current;
  };

  function renderGarage() {
    const garage = garageElement();
    if (!garage) return;

    const enabled = garage.dataset.bmMyAutoEnabled !== 'false';
    const hideEmpty = garage.dataset.bmMyAutoHideEmpty !== 'false';
    const title = garage.querySelector('[data-bm-vehicle-title]');
    const meta = garage.querySelector('[data-bm-vehicle-meta]');
    const stockLink = garage.querySelector('[data-bm-my-auto-stock-link]');
    const stockLabel = garage.querySelector('[data-bm-my-auto-stock-label]');
    const changeLink = garage.querySelector('[data-bm-vehicle-change-link]');
    const active = getActive();

    if (!enabled) {
      garage.hidden = true;
      return;
    }

    if (!active) {
      garage.classList.add('is-empty');
      garage.hidden = hideEmpty;
      if (title) title.textContent = garage.dataset.bmMyAutoEmptyTitle || 'Kies jouw auto';
      if (meta) meta.textContent = garage.dataset.bmMyAutoEmptyMeta || '';
      if (stockLink) { stockLink.hidden = false; stockLink.href = selectorUrl(); }
      if (stockLabel) stockLabel.textContent = garage.dataset.bmMyAutoEmptyActionLabel || 'Kies auto';
      if (changeLink) changeLink.hidden = true;
      setGarageImage(garage, null, '');
      return;
    }

    garage.classList.remove('is-empty');
    garage.hidden = false;
    if (changeLink) {
      changeLink.hidden = false;
      changeLink.href = selectorUrl();
    }
    const destination = active.newVehicle ? nativeExactVehicleHref(active.newVehicle) : active.generation?.url;
    const stockDestination = destination
      ? (active.newVehicle ? `${destination}#bm-exact-vehicle-new` : destination)
      : (active.newVehicle ? draftVehicleResultsHref() : '/collections/alle-velgen');
    const destinationIsCurrentPage = Boolean(destination)
      && normalizePath(destination) === normalizePath(window.location.pathname);
    if (stockLink) {
      stockLink.href = stockDestination;
      stockLink.hidden = destinationIsCurrentPage;
    }
    if (stockLabel) stockLabel.textContent = destination
      ? (garage.dataset.bmMyAutoStockLabel || 'Bekijk passende velgen')
      : (active.newVehicle ? 'Bekijk velgen voor deze auto' : 'Bekijk gebruikte velgensets');
    if (title) title.textContent = vehicleTitle(active);

    const showMeta = garage.dataset.bmMyAutoShowMeta !== 'false';
    const showCodes = garage.dataset.bmMyAutoShowCodes === 'true';
    const fallbackMeta = garage.dataset.bmMyAutoFallbackMeta || '';
    if (meta) meta.textContent = showMeta ? (vehicleMeta(active, showCodes) || fallbackMeta) : '';

    const generationId = String(active.generation?.id || '');
    const cachedImage = active.generation?.image || readImageCache()[generationId] || '';
    setGarageImage(garage, active, cachedImage);

    const needsDetails = Boolean(generationId) && (
      (garage.dataset.bmMyAutoShowImage !== 'false' && !cachedImage)
      || (showMeta && (!active.generation?.years || (showCodes && !active.generation?.codes)))
    );
    if (!needsDetails) return;

    const requestId = ++garageDetailRequest;
    fetchGenerationDetails(active).then((details) => {
      if (requestId !== garageDetailRequest) return;
      const current = getActive();
      if (!current || String(current.generation?.id || '') !== generationId) return;
      const enriched = persistResolvedDetails(current, details);
      setGarageImage(garage, enriched, details.image || enriched?.generation?.image || '');
      if (meta && showMeta) meta.textContent = vehicleMeta(enriched, showCodes) || fallbackMeta;
    }).catch(() => {});
  }

  function createCompatibilityBlock() {
    const block = document.createElement('div');
    block.className = 'bm-vehicle-compatibility';
    block.setAttribute('data-bm-vehicle-compatibility', '');
    block.innerHTML = `
      <span class="bm-vehicle-compatibility__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 16h14M7 16l1-5h8l1 5M8.5 11l1-3h5l1 3M7 19h.01M17 19h.01" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      <span class="bm-vehicle-compatibility__body"><strong data-bm-compat-title>Controleer passing voor jouw auto</strong><span data-bm-compat-detail>Kies je auto om deze set met jouw generatie te vergelijken.</span></span>
      <a data-bm-compat-action href="#voertuigselector">Kies auto</a>`;
    return block;
  }

  function renderProductCompatibility() {
    const explicitContext = document.querySelector('[data-bm-product-fitment-context]');
    const buybox = document.querySelector('.bm-buybox');
    if (!buybox) return;
    const fitmentLinks = [...buybox.querySelectorAll('.bm-fitment-chips a[href]')];
    if (!explicitContext && !fitmentLinks.length) return;
    let block = buybox.querySelector('[data-bm-vehicle-compatibility]');
    if (!block) {
      block = createCompatibilityBlock();
      const catalog = buybox.querySelector('.bm-catalog-fitment');
      if (catalog) catalog.insertAdjacentElement('afterend', block);
      else buybox.appendChild(block);
    }
    const title = block.querySelector('[data-bm-compat-title]');
    const detail = block.querySelector('[data-bm-compat-detail]');
    const action = block.querySelector('[data-bm-compat-action]');
    const active = getActive();
    const source = explicitContext?.dataset.source || 'internal-generation';
    const fitmentUrls = explicitContext?.dataset.fitmentUrls || fitmentLinks.map((link) => link.href).join('|');
    block.classList.remove('is-match', 'is-warning', 'is-neutral');
    if (action) action.href = selectorUrl();
    if (!active) {
      block.classList.add('is-neutral');
      if (title) title.textContent = 'Past deze set op jouw auto?';
      if (detail) detail.textContent = 'Selecteer merk, model en generatie om de passing te controleren.';
      if (action) action.textContent = 'Kies auto';
      return;
    }
    const resolver = compatibilityResolvers.get(source);
    const result = resolver ? resolver(active, {
      fitmentIds: explicitContext?.dataset.fitmentIds || '',
      fitmentUrls,
      productId: explicitContext?.dataset.productId || '',
      productType: explicitContext?.dataset.productType || 'Velgenset'
    }) : null;
    const label = vehicleLabel(active);
    if (result === true) {
      block.classList.add('is-match');
      if (title) title.textContent = 'Past volgens onze database op jouw auto';
      if (detail) detail.textContent = label;
      if (action) action.textContent = 'Wijzig';
    } else if (result === false) {
      block.classList.add('is-warning');
      if (title) title.textContent = 'Niet gekoppeld aan jouw gekozen generatie';
      if (detail) detail.textContent = `${label} · controleer de passing vóór bestelling.`;
      if (action) action.textContent = 'Wijzig';
    } else {
      block.classList.add('is-neutral');
      if (title) title.textContent = 'Pasvorm nog te controleren';
      if (detail) detail.textContent = `${label} · controleer de pasvorm per velg.`;
      if (action) action.textContent = 'Wijzig';
    }
  }

  const findChoice = (root, type, id) => !root || !id ? null : [...root.querySelectorAll(`.bm-choice[data-choice="${type}"]`)].find((choice) => String(choice.dataset.id || '') === String(id)) || null;

  const waitForChoice = (root, type, id, timeout = 5000) => new Promise((resolve) => {
    const immediate = findChoice(root, type, id);
    if (immediate) { resolve(immediate); return; }
    let settled = false;
    const observer = new MutationObserver(() => {
      const choice = findChoice(root, type, id);
      if (choice && !settled) { settled = true; observer.disconnect(); resolve(choice); }
    });
    observer.observe(root, { childList: true, subtree: true });
    window.setTimeout(() => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      resolve(null);
    }, timeout);
  });

  async function restoreSelector() {
    const active = getActive();
    const root = document.querySelector('.bm-fitment');
    if (!active || !root || root.dataset.bmGarageRestored === 'true') return;
    if (root.dataset.bmSavedVehicleView === 'true') { root.dataset.bmGarageRestored = 'true'; return; }
    root.dataset.bmGarageRestored = 'loading';
    const brandChoice = findChoice(root, 'brand', active.brand?.id);
    if (!brandChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    brandChoice.click();
    const modelChoice = await waitForChoice(root, 'model', active.model?.id);
    if (!modelChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    modelChoice.click();
    const generationChoice = await waitForChoice(root, 'generation', active.generation?.id);
    if (!generationChoice) { root.dataset.bmGarageRestored = 'false'; return; }
    generationChoice.click();
    root.dataset.bmGarageRestored = 'true';
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-bm-vehicle-clear]')) clear();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    memoryState = { version: VERSION, active: null };
    readState();
    notify();
  });

  // A restored document retains its old memory and DOM; storage events and
  // ready/connected callbacks need not run again. Re-read the existing garage
  // and notify its consumers, including the exact NEW product fitment reader.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    memoryState = { version: VERSION, active: null };
    readState();
    notify(true);
  });

  // Shopify Theme Editor replaces section markup without a full page reload.
  // Re-render from the central vehicle state so every editor setting is previewable immediately.
  document.addEventListener('shopify:section:load', () => {
    renderGarage();
    renderProductCompatibility();
  });

  const exactVehiclePhotoRequests = new Map();
  const loadExactVehiclePhoto = (vehicle, themeRole, themeId) => {
    const exact = vehicle?.newVehicle;
    if (!exact?.vehicleId) return Promise.resolve('');
    // The homepage selector is already navigating to this exact page. Do not
    // fetch its complete HTML a second time just to discover the hero photo.
    if (document.querySelector('[data-bm-home-podium="true"][data-bm-vehicle-selection-pending="true"]')) return Promise.resolve('');
    const key = String(exact.vehicleId);
    if (exactVehiclePhotoRequests.has(key)) return exactVehiclePhotoRequests.get(key);
    const pending = (async () => {
      try {
        const client = window.BandenmeestersNewClient;
        let page = exact.vehiclePageUrl || '';
        if (!page && exact.brandRef && exact.modelRef && client?.vehicleTypes) {
          const payload = await client.vehicleTypes(exact.brandRef, exact.modelRef);
          const type = payload.types?.find(row => row.vehicleId === exact.vehicleId);
          page = type?.vehiclePageUrl || client.unpublishedVehiclePagePreviewUrl?.(type, { themeRole }) || '';
        }
        if (!page) return '';
        const target = new URL(page, window.location.origin);
        // Shopify's theme editor adds a session tracking parameter to native links.
        // It is not part of vehicle identity and must not reach the media request.
        if (['unpublished','development'].includes(String(themeRole).toLowerCase())) target.searchParams.delete('oseid');
        if (['unpublished','development'].includes(String(themeRole).toLowerCase()) && /^\d+$/.test(String(themeId || ''))) target.searchParams.set('preview_theme_id', String(themeId));
        const primary = document.querySelector('[data-bm-mobile-menu-overlay]')?.dataset.bmPrimaryShopDomain || '';
        const preview = window.location.hostname === 'shopifypreview.com' || window.location.hostname.endsWith('.shopifypreview.com');
        if (target.protocol !== 'https:' || target.username || target.password || target.port || target.hash
          || (target.origin !== window.location.origin && !(preview && target.hostname === primary))
          || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/[^/]+\/[^/]+\/?$/.test(target.pathname)
          || [...target.searchParams.keys()].some(name => name !== 'preview_theme_id')
          || (target.search && !/^\d+$/.test(target.searchParams.get('preview_theme_id') || ''))) return '';
        const response = await fetch(target.pathname + target.search, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) return '';
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const owner = doc.querySelector('.bm-exact-vehicle[data-vehicle-id]');
        if (owner?.dataset.vehicleId !== exact.vehicleId) return '';
        const marker = owner.querySelector('[data-bm-canonical-vehicle-image]');
        if (marker?.dataset.bmCanonicalVehicleHref?.replace(/\/$/, '') !== target.pathname.replace(/\/$/, '')) return '';
        const raw = marker?.dataset.bmCanonicalVehicleImage;
        if (!raw) return '';
        const photo = new URL(raw, window.location.origin);
        if (photo.protocol !== 'https:' || photo.username || photo.password
          || !(photo.origin === window.location.origin || photo.hostname === 'cdn.shopify.com'
            || (primary && photo.hostname === primary && photo.pathname.startsWith('/cdn/')))) return '';
        return photo.href;
      } catch (_) { return ''; }
    })();
    exactVehiclePhotoRequests.set(key, pending);
    return pending;
  };

  const photoFor = (vehicle, { themeRole = '', themeId = '' } = {}) => {
    const raw = vehicle?.newVehicle?.image || vehicle?.generation?.image || '';
    try {
      const primary = document.querySelector('[data-bm-mobile-menu-overlay]')?.dataset.bmPrimaryShopDomain || '';
      const url = new URL(raw, window.location.origin);
      if (raw && url.protocol === 'https:' && !url.username && !url.password
        && (url.hostname === 'cdn.shopify.com' || (url.origin === window.location.origin && url.pathname.startsWith('/cdn/'))
          || (url.hostname === primary && url.pathname.startsWith('/cdn/')))) return Promise.resolve(url.href);
    } catch (_) {}
    return loadExactVehiclePhoto(vehicle, themeRole, themeId);
  };

  window.BandenmeestersVehicle = {
    photoFor,
    version: VERSION,
    storageKey: STORAGE_KEY,
    getActive,
    getNewVehicle,
    refreshNewVehicle,
    setActive,
    clear,
    mergeProviderIds,
    isSelectorAction,
    vehicleLabel,
    shortGenerationName,
    vehicleMeta,
    selectorUrl,
    registerCompatibilityResolver
  };

  // The exact Shopify vehicle page already contains its canonical image and
  // owner identity. Remember that verified photo for the next homepage visit;
  // another HTML fetch is unnecessary and would delay the first car paint.
  const rememberExactPagePhoto = () => {
    const active = getActive();
    const exact = active?.newVehicle;
    const owner = document.querySelector('.bm-exact-vehicle[data-vehicle-id][data-bm-owner-gid]');
    const marker = owner?.querySelector('[data-bm-canonical-vehicle-image]');
    if (!exact?.vehicleId || owner?.dataset.vehicleId !== exact.vehicleId ||
      !/^gid:\/\/shopify\/Metaobject\/[1-9]\d*$/.test(owner.dataset.bmOwnerGid || '') ||
      !marker?.dataset.bmCanonicalVehicleImage) return;
    try {
      const expected = nativeExactVehicleHref(exact);
      if (!expected || new URL(expected, window.location.origin).pathname !== window.location.pathname ||
          marker.dataset.bmCanonicalVehicleHref?.replace(/\/$/, '') !== window.location.pathname.replace(/\/$/, '')) return;
      const photo = new URL(marker.dataset.bmCanonicalVehicleImage, window.location.origin);
      const primary = document.querySelector('[data-bm-mobile-menu-overlay]')?.dataset.bmPrimaryShopDomain || '';
      if (photo.protocol !== 'https:' || photo.username || photo.password || photo.port ||
        !(photo.hostname === 'cdn.shopify.com' ||
          (photo.origin === window.location.origin && photo.pathname.startsWith('/cdn/')) ||
          (primary && photo.hostname === primary && photo.pathname.startsWith('/cdn/')))) return;
      if (exact.image !== photo.href) {
        const updated = cleanNewVehicle({ ...exact, image: photo.href });
        if (updated) writeState({ version: VERSION, active: { ...active, newVehicle: updated } });
      }
    } catch (_) { /* Never persist a noncanonical or mismatched photo. */ }
  };

  const init = () => {
    readState();
    rememberExactPagePhoto();
    renderGarage();
    renderProductCompatibility();
    restoreSelector();
    document.dispatchEvent(new CustomEvent('bm:vehicle:ready', { detail: { vehicle: getActive() } }));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

// Product → vehicle selector → product return flow.
// The existing active vehicle is intentionally preserved while the shopper drafts another selection.
(() => {
  const RETURN_KEY = 'bm_vehicle_return_url';
  let fitmentNavigationRequest = 0;
  const currentPath = () => window.location.pathname + window.location.search;
  const safeReturn = value => {
    if (!value || !value.startsWith('/') || value.startsWith('//')) return '';
    try {
      const url = new URL(value, window.location.origin || 'https://bandenmeesters.be');
      if (url.origin !== (window.location.origin || 'https://bandenmeesters.be') ||
        !(url.pathname === '/cart' || url.pathname.startsWith('/products/'))) return '';
      return url.pathname + url.search;
    } catch (_) { return ''; }
  };
  const setSelectionReturn = value => {
    const path = safeReturn(value);
    if (!path) return false;
    try { window.sessionStorage.setItem(RETURN_KEY, path); return true; } catch (_) { return false; }
  };
  const consumeSelectionReturn = () => {
    try {
      const path = safeReturn(window.sessionStorage.getItem(RETURN_KEY) || '');
      window.sessionStorage.removeItem(RETURN_KEY);
      return path;
    } catch (_) { return ''; }
  };
  Object.assign(window.BandenmeestersVehicle, { setSelectionReturn, consumeSelectionReturn });

  document.addEventListener('click', (event) => {
    const searchLink = event.target.closest?.('[data-bm-my-auto-stock-link], [data-bm-menu-vehicle-results], [data-bm-mobile-vehicle-action], a[data-summary-button]');
    const selected = window.BandenmeestersVehicle.getNewVehicle?.();
    const explicitSummary = searchLink?.matches?.('a[data-summary-button]')
      && searchLink.closest('[data-bm-home-vehicle-selector]')?.dataset.bmGenerationState === 'ready';
    if (searchLink && selected?.vehicleId && window.BandenmeestersNewClient?.searchVehicleFitment
      && (explicitSummary || searchLink.matches?.('[data-bm-my-auto-stock-link]') || searchLink.matches?.('[data-bm-menu-vehicle-results]') || searchLink.matches?.('[data-bm-mobile-vehicle-action]'))
      && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey
      && (!event.button || event.button === 0) && (!searchLink.target || searchLink.target === '_self')) {
      // This link is native navigation. The search is a keepalive POST that
      // does not own the browser location or intercept Back/Forward.
      void window.BandenmeestersNewClient.searchVehicleFitment({ vehicleId: selected.vehicleId })
        .catch(() => {
          if (window.BandenmeestersVehicle.getNewVehicle?.()?.vehicleId === selected.vehicleId) {
            window.dispatchEvent(new CustomEvent('bm-new:fitment-status', { detail: {
              vehicleId: selected.vehicleId, status: 'TEMPORARILY_UNAVAILABLE', coverage: null,
            } }));
          }
        });
      return;
    }
    if (!document.body.classList.contains('template-product')) return;
    const link = event.target.closest?.('a[href*="#voertuigselector"], [data-bm-product-vehicle-selector], [data-bm-compat-action]');
    if (!link || !window.BandenmeestersVehicle.isSelectorAction(link)) return;
    setSelectionReturn(currentPath());
  }, true);

  window.addEventListener('bm:vehicle:change', (event) => {
    if (!event.detail?.evidenceRefresh) ++fitmentNavigationRequest;
    if (!event.detail?.vehicle || event.detail.evidenceRefresh) return;
    const returnUrl = consumeSelectionReturn();
    if (!returnUrl) return;
    if (returnUrl !== currentPath()) window.setTimeout(() => window.location.assign(returnUrl), 70);
  });
})();
